/**
 * The Invoiced Book - billed-work history at INVOICED. Per-invoice
 * billing detail (expandable schedule per project), monthly billed
 * revenue, top clients by billed value. Team/client drill-down
 * cross-filters everything (?team=…&client=…&book=invoiced).
 */
import { useEffect, useMemo, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";
import {
  TeamPie, QualQuantMix, TopClients, MonthlyTrend,
  fmtGbp, fmtGbpShort, fmtDate, teamColor,
} from "./CommissionedPanels";
import { clientKey, buildClientIndex } from "./clientGrouping";
import {
  groupAgg, SortBtn, FilterChip, InvoiceSchedule, InvoiceToggleCell,
} from "./bookShared";

const API = process.env.REACT_APP_BACKEND_URL;

export default function InvoicedBook() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState("final_invoice");
  const [sortDir, setSortDir] = useState("desc");
  const [expandedCid, setExpandedCid] = useState(null);
  const [limit, setLimit] = useState(50);
  const [searchParams, setSearchParams] = useSearchParams();
  const teamFilter = searchParams.get("team");
  const clientFilter = searchParams.get("client");

  useEffect(() => {
    axios.get(`${API}/api/proposals/pipeline/invoiced-book`)
      .then((r) => { setData(r.data); setError(""); })
      .catch((e) => setError(e.response?.data?.detail || "Could not load the invoiced book."))
      .finally(() => setLoading(false));
  }, []);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value == null) next.delete(key);
    else next.set(key, value);
    if (next.get("team") || next.get("client")) next.set("book", "invoiced");
    else next.delete("book");
    setSearchParams(next, { replace: true });
  };
  const toggleTeam = (t) => setFilter("team", teamFilter === t ? null : t);
  const toggleClient = (c) => setFilter("client", clientFilter === c ? null : c);
  const clearAll = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("team");
    next.delete("client");
    next.delete("book");
    setSearchParams(next, { replace: true });
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  const clientIndex = useMemo(() => buildClientIndex(data?.items || []), [data]);

  const view = useMemo(() => {
    const all = data?.items || [];
    const clientScoped = clientFilter ? all.filter((i) => clientKey(i) === clientFilter) : all;
    const teamScoped = teamFilter ? all.filter((i) => i.team === teamFilter) : all;
    const filtered = teamFilter ? clientScoped.filter((i) => i.team === teamFilter) : clientScoped;

    const byTeam = groupAgg(clientScoped, (i) => i.team)
      .map((g) => ({ team: g.key, count: g.count, value: g.value }))
      .sort((a, b) => b.count - a.count);

    let topClients = groupAgg(teamScoped, clientKey)
      .map((g) => ({
        key: g.key,
        company: clientIndex.get(g.key)?.label || g.key,
        variants: clientIndex.get(g.key)?.variants || [],
        count: g.count,
        value: g.value,
      }))
      .sort((a, b) => b.value - a.value);
    const head = topClients.slice(0, 8);
    if (clientFilter && !head.some((c) => c.key === clientFilter)) {
      const sel = topClients.find((c) => c.key === clientFilter);
      if (sel) head.push(sel);
    }
    topClients = head;

    const qualQuant = groupAgg(filtered, (i) => i.qual_quant || "Unspecified")
      .map((g) => ({ key: g.key, count: g.count }))
      .sort((a, b) => b.count - a.count);

    // Monthly billed revenue re-scoped to the filter, from per-invoice dates.
    const monthKeys = (data?.monthly || []).map((m) => m.month);
    const mg = Object.fromEntries(monthKeys.map((k) => [k, { count: 0, value: 0 }]));
    for (const i of filtered) {
      for (const inv of i.invoices || []) {
        const k = (inv.date || "").slice(0, 7);
        if (mg[k]) { mg[k].count += 1; mg[k].value += inv.value || 0; }
      }
    }
    const monthly = monthKeys.map((k) => ({ month: k, ...mg[k] }));

    const values = filtered.filter((i) => i.value_gbp).map((i) => i.value_gbp);
    const invoicesIssued = filtered.reduce((s, i) => s + (i.invoice_count || 0), 0);
    const totals = {
      count: filtered.length,
      billed_value: values.reduce((s, v) => s + v, 0),
      invoices_issued: invoicesIssued,
      multi: filtered.filter((i) => (i.invoice_count || 0) > 1).length,
    };

    return { filtered, byTeam, topClients, qualQuant, monthly, totals };
  }, [data, teamFilter, clientFilter, clientIndex]);

  const items = useMemo(() => {
    const dir = sortDir === "desc" ? -1 : 1;
    return [...view.filtered].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      const cmp = typeof va === "string" || typeof vb === "string"
        ? String(va || "").localeCompare(String(vb || ""))
        : (va ?? -Infinity) - (vb ?? -Infinity);
      return dir * cmp;
    });
  }, [view.filtered, sortKey, sortDir]);

  const shown = items.slice(0, limit);
  const t = view.totals;
  const hasFilter = Boolean(teamFilter || clientFilter);

  return (
    <section
      id="invoiced-book"
      data-testid="invoiced-book"
      className="bg-white border border-stone-200 rounded-2xl overflow-hidden shadow-sm mt-6 scroll-mt-20"
    >
      <header className="px-5 pt-4 pb-3 border-b border-stone-100">
        <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-teal-700 mb-0.5">
          The invoiced book
        </p>
        <p className="text-sm text-stone-600">
          Every project fully <span className="font-medium text-slate-800">billed and closed</span> - the complete invoice history behind each one. Click a team or client to drill down.
        </p>
      </header>

      {loading && (
        <div className="flex items-center gap-2 text-stone-500 text-sm px-5 py-8" data-testid="invoiced-book-loading">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading the invoiced book…
        </div>
      )}
      {!loading && error && (
        <div className="mx-5 my-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2" data-testid="invoiced-book-error">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {hasFilter && (
            <div
              data-testid="invoiced-filter-bar"
              className="flex flex-wrap items-center gap-2 px-5 py-2.5 border-b border-teal-100 bg-teal-50/40"
            >
              <span className="text-[10px] font-mono uppercase tracking-wider text-stone-500">
                Drilled into
              </span>
              {teamFilter && (
                <FilterChip
                  label={`Team: ${teamFilter}`}
                  onClear={() => setFilter("team", null)}
                  testid="invoiced-filter-chip-team"
                  tone="teal"
                />
              )}
              {clientFilter && (
                <FilterChip
                  label={`Client: ${clientIndex.get(clientFilter)?.label || clientFilter}`}
                  onClear={() => setFilter("client", null)}
                  testid="invoiced-filter-chip-client"
                  tone="teal"
                />
              )}
              <button
                type="button"
                onClick={clearAll}
                data-testid="invoiced-filter-clear"
                className="text-xs font-semibold text-teal-800 hover:text-teal-600 transition-colors ml-1"
              >
                clear all
              </button>
            </div>
          )}

          {/* KPI strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-stone-100">
            {[
              { label: "Invoiced projects", value: t.count?.toLocaleString() ?? "-", testid: "invoiced-book-kpi-count" },
              { label: "Billed value", value: fmtGbpShort(t.billed_value), tone: "text-teal-700", testid: "invoiced-book-kpi-value" },
              { label: "Invoices issued", value: t.invoices_issued?.toLocaleString() ?? "-", tone: "text-[#1B4F8A]", testid: "invoiced-book-kpi-invoices" },
              { label: "Multi-invoice projects", value: t.multi?.toLocaleString() ?? "-", tone: "text-[#1B4F8A]", testid: "invoiced-book-kpi-multi" },
            ].map((k) => (
              <div key={k.label} className="bg-white px-4 py-3" data-testid={k.testid}>
                <p className="text-[10px] font-mono uppercase tracking-wider text-stone-500 mb-1">{k.label}</p>
                <p className={`text-2xl font-medium tabular-nums leading-none ${k.tone || "text-slate-900"}`}>{k.value}</p>
              </div>
            ))}
          </div>

          {/* Charts grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 px-5 py-4 border-t border-stone-100 bg-stone-50/50">
            <TeamPie byTeam={view.byTeam} selected={teamFilter} onSelect={toggleTeam} idPrefix="invoiced" />
            <div className="flex flex-col gap-4">
              <QualQuantMix mix={view.qualQuant} />
            </div>
            <TopClients
              clients={view.topClients}
              selected={clientFilter}
              onSelect={toggleClient}
              title="Top clients by billed value"
              idPrefix="invoiced"
            />
            <div className="md:col-span-2 xl:col-span-3">
              <MonthlyTrend
                monthly={view.monthly}
                title="Invoices issued per month · last 12 months"
                color="#0D9488"
                idPrefix="invoiced"
              />
            </div>
          </div>

          {/* Project table */}
          <div className="overflow-x-auto border-t border-stone-100">
            <table className="w-full text-sm" data-testid="invoiced-book-table">
              <thead>
                <tr className="text-[10px] font-mono uppercase tracking-wider text-stone-400 border-b border-stone-100">
                  <th className="px-5 py-2.5 text-left font-medium">Client / project</th>
                  <th className="px-3 py-2.5 text-left font-medium">Team</th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    <SortBtn label="Billed £" k="value_gbp" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="invoiced-book-sort-value" />
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    <SortBtn label="First invoice" k="first_invoice" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="invoiced-book-sort-first" />
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    <SortBtn label="Final invoice" k="final_invoice" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="invoiced-book-sort-final" />
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    <SortBtn label="Invoices" k="invoice_count" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="invoiced-book-sort-invoices" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((item, i) => (
                  <Fragment key={item.cid ?? i}>
                  <tr
                    data-testid={`invoiced-book-row-${item.cid}`}
                    className="border-b border-stone-50 hover:bg-stone-50/60 transition-colors"
                  >
                    <td className="px-5 py-2.5">
                      <button
                        type="button"
                        onClick={() => toggleClient(clientKey(item))}
                        data-testid={`invoiced-row-client-${item.cid}`}
                        title={`Drill into ${item.client_company || "Unknown"}`}
                        className="font-medium text-slate-900 leading-snug truncate max-w-[240px] block text-left hover:text-[#1B4F8A] hover:underline transition-colors"
                      >
                        {item.client_company || "Unknown client"}
                      </button>
                      <p className="text-[11px] text-stone-500 truncate max-w-[240px]">
                        {item.topic || item.project_name || ""}
                      </p>
                      <p className="text-[10px] font-mono text-stone-400 mt-0.5">
                        {item.cognito_admin ? (
                          <a
                            href={item.cognito_admin}
                            target="_blank"
                            rel="noreferrer"
                            title="Open in Cognito (admin)"
                            className="hover:text-[#1B4F8A] hover:underline transition-colors"
                          >
                            #{item.cid}
                          </a>
                        ) : (
                          <span>#{item.cid}</span>
                        )}
                        {item.project_number && <span> · {item.project_number}</span>}
                      </p>
                    </td>
                    <td className="px-3 py-2.5">
                      <button
                        type="button"
                        onClick={() => toggleTeam(item.team)}
                        data-testid={`invoiced-row-team-${item.cid}`}
                        title={`Drill into team ${item.team}`}
                        className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-700 hover:text-[#1B4F8A] transition-colors"
                      >
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ background: teamColor(item.team, 0) }}
                        />
                        {item.team}
                      </button>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-700">
                      {fmtGbp(item.value_gbp)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-xs text-stone-500 whitespace-nowrap">
                      {fmtDate(item.first_invoice)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-xs text-slate-700 whitespace-nowrap">
                      {fmtDate(item.final_invoice)}
                    </td>
                    <InvoiceToggleCell
                      item={{ ...item, invoiced_count: item.invoice_count, expected_invoices: item.expected_invoices ?? item.invoice_count }}
                      expanded={expandedCid === item.cid}
                      onToggle={() => setExpandedCid(expandedCid === item.cid ? null : item.cid)}
                      idPrefix="invoiced"
                    />
                  </tr>
                  {expandedCid === item.cid && (
                    <tr data-testid={`invoiced-invoice-schedule-row-${item.cid}`} className="bg-stone-50/60">
                      <td colSpan={6} className="px-5 py-3">
                        <InvoiceSchedule invoices={item.invoices} expected={item.expected_invoices} idPrefix="invoiced" cid={item.cid} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
            {shown.length === 0 && (
              <p className="px-5 py-8 text-sm text-stone-400" data-testid="invoiced-book-empty">
                {hasFilter
                  ? "No invoiced projects match the current drill-down."
                  : "No projects at INVOICED in the Schedule of Work."}
              </p>
            )}
            {items.length > shown.length && (
              <div className="px-5 py-3 border-t border-stone-100">
                <button
                  type="button"
                  onClick={() => setLimit((l) => l + 100)}
                  data-testid="invoiced-book-show-more"
                  className="text-xs font-semibold text-teal-800 hover:text-teal-600 transition-colors"
                >
                  Show more ({items.length - shown.length} remaining)
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
